import { zodResolver } from '@hookform/resolvers/zod'
import { DatabaseIcon, PlusIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { PmsCompany, useCreatePmsCompany } from '@/api/pms'
import { PasswordInputFormField } from '@/components/form'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { toastFactory } from '@/factories'
import { getApiErrorMessage } from '@/utils/error'

import { PersonFormLayout } from '../../../../shared/person-form-layout'
import {
  makePmsCompanySchema,
  PMS_COMPANY_FIELDS,
  PmsCompanyField,
  PmsConnectProvider,
} from './pms-tab.constants'

interface PmsCompanyFormModalProps {
  onClose: VoidFunction
  onCreated: (company: PmsCompany) => void
  provider: PmsConnectProvider
}

export const PmsCompanyFormModal = ({ onClose, onCreated, provider }: PmsCompanyFormModalProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { mutateAsync: createCompany } = useCreatePmsCompany()

  const fields = PMS_COMPANY_FIELDS[provider]
  const schema = useMemo(
    () => makePmsCompanySchema(provider, t('common:validation.required')),
    [provider, t],
  )

  const form = useForm<Record<PmsCompanyField, string>>({
    defaultValues: Object.fromEntries(fields.map((field) => [field, ''])) as Record<
      PmsCompanyField,
      string
    >,
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  const { isSubmitting } = form.formState

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const company = await createCompany({ data: values, provider })
      toastFactory.success(t('organisation:places.integrations.companyModal.toasts.created'))
      onCreated(company)
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  })

  return (
    <Modal
      autoWidth
      title={t('organisation:places.integrations.companyModal.headerTitle')}
      headerIcon={DatabaseIcon}
      footer={
        <>
          <Button use="secondary" onClick={onClose}>
            {t('common:actions.back')}
          </Button>
          <Button loading={isSubmitting} use="primary" onClick={onSubmit}>
            {t('common:actions.add')}
            <Icon icon={PlusIcon} />
          </Button>
        </>
      }
      onClose={onClose}
    >
      <PersonFormLayout form={form} onSubmit={onSubmit}>
        {fields.map((field) => {
          const labelKey =
            provider === 'entrata' && field === 'username' ? 'entrataUsername' : field

          return field === 'password' ? (
            <PasswordInputFormField
              key={field}
              withReservedFooter
              required
              autoComplete="new-password"
              label={t(`organisation:places.integrations.companyModal.fields.${labelKey}.label`)}
              name={field}
              placeholder={t(
                `organisation:places.integrations.companyModal.fields.${labelKey}.placeholder`,
              )}
            />
          ) : (
            <TextInputFormField
              key={field}
              withReservedFooter
              required
              autoComplete="off"
              label={t(`organisation:places.integrations.companyModal.fields.${labelKey}.label`)}
              name={field}
              placeholder={t(
                `organisation:places.integrations.companyModal.fields.${labelKey}.placeholder`,
              )}
            />
          )
        })}
      </PersonFormLayout>
    </Modal>
  )
}
